import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { allGroupMemberRoles, GroupMemberRole, GroupType } from "@domain/group/group";
import { GroupMember } from "@domain/group/groupMember";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { desktop, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RadioButton } from "@ui/components/inputs/radio";
import { row, Stack, stack } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";

interface GroupMemberAccessModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	groupId: string;
	groupName: string;
	groupType: GroupType;
	member: GroupMember;
}
export const GroupMemberAccessModal: React.FC<GroupMemberAccessModalProps> = ({
	close,
	groupId,
	groupName,
	groupType,
	member,
	...htmlProps
}) => {
	const [selectedRole, setSelectedRole] = useState<GroupMemberRole>();
	const { groupService } = useServices();
	const { loading, run } = useAsync(async () => {
		if (!selectedRole) {
			return;
		}
		try {
			await groupService.acceptMembership(groupId, member.id, selectedRole);
			createToast(I18n.t("success"), I18n.t(`group.${groupType}.grant.success`, { name: groupName }), "success");
			close();
		} catch {
			createToast(I18n.t("error"), I18n.t("group.grant.error"), "error");
		}
	}, [groupId, groupName, member, selectedRole]);

	return (
		<Container {...htmlProps} close={close} title={groupName}>
			<Stack gap={20}>
				<UserView user={member}>
					<span> {I18n.t(`group.${groupType}.grant.info`)}</span>
				</UserView>
				<PrimaryMedium>{I18n.t("group.grant.accept", { name: member.primaryName })}</PrimaryMedium>
				<Stack gap={10}>
					{allGroupMemberRoles.map((role) => (
						<RadioButton
							key={role}
							id={role}
							value={role}
							label={I18n.t(`group.${groupType}.role.${role}`)}
							name="role"
							onChange={(v) => setSelectedRole(v as GroupMemberRole)}
							checked={role === selectedRole}
						/>
					))}
				</Stack>
			</Stack>
			<Actions>
				<SecondaryButton onClick={close}>{I18n.t("cancel")}</SecondaryButton>
				<LoadingButton loading={loading} disabled={!selectedRole} onClick={run}>
					{I18n.t("validate")}
				</LoadingButton>
			</Actions>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${stack(40)};
`;

const Actions = styled.div`
	margin: 0 auto;

	@media ${desktop} {
		${row(10, "space-between", "center")};
	}
	@media ${mobile} {
		${stack(10, "flex-start", "center")};
	}
`;
